import { useActiveCode, useSandpack } from "@codesandbox/sandpack-react";
import Editor from "@monaco-editor/react";
import { useRef } from "react";
export const getLanguageOfFile = (filePath) => {
  const extensionDotIndex = filePath?.lastIndexOf(".");
  const extension = filePath?.slice(extensionDotIndex + 1);

  switch (extension) {
    case "js":
    case "jsx":
    case "ts":
    case "tsx":
      return "javascript";
    case "vue":
    case "html":
      return "html";
    case "css":
    case "scss":
    case "less":
      return "css";
    case "json":
      return "json";
    default:
      return extension;
  }
};

export default function MonacoEditor() {
  const { code, updateCode } = useActiveCode();
  const { sandpack } = useSandpack();
  const language = getLanguageOfFile(sandpack.activeFile);
  const editorRef = useRef(null);
  const options = {
    selectOnLineNumbers: true,
    mouseWheelZoom: true,
    fontSize: 18,
    contextmenu: false,
    formatOnType: true,
    smoothScrolling: true,
    wordWrap: "on",
    quickSuggestions: {
      other: true,
      comments: false,
      strings: true,
    },
    suggestOnTriggerCharacters: true,
    acceptSuggestionOnEnter: "on",
    autoClosingBrackets: "always",
    autoClosingQuotes: "always",
    autoSurround: "languageDefined",
    bracketPairColorization: {
      enabled: true,
    },
    suggest: {
      showKeywords: true,
      showSnippets: true,
      showFunctions: true,
      showConstructors: true,
      showFields: true,
      showVariables: true,
      showClasses: true,
      showStructs: true,
      showInterfaces: true,
      showModules: true,
      showProperties: true,
      showEvents: true,
      showOperators: true,
      showUnits: true,
      showValues: true,
      showConstants: true,
      showEnums: true,
      showEnumMembers: true,
      showKeywords: true,
      showWords: true,
      showColors: true,
      showFiles: true,
      showReferences: true,
      showFolders: true,
      showTypeParameters: true,
      showIssues: true,
      showUsers: true,
      showSnippets: true,
    },
  };

  const onMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.focus();

    // Enhanced TypeScript/JSX configuration
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
      reactNamespace: "React",
      allowNonTsExtensions: true,
      allowJs: true,
      checkJs: true,
      target: monaco.languages.typescript.ScriptTarget.Latest,
      lib: ["dom", "es2015"],
    });

    // Set diagnostics options
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });

    // Register completion provider for automatic tag closing
    ["javascript", "javascriptreact", "typescript", "typescriptreact"].forEach(
      (lang) => {
        monaco.languages.registerCompletionItemProvider(lang, {
          triggerCharacters: [">", " "],
          provideCompletionItems: (model, position) => {
            const textUntilPosition = model.getValueInRange({
              startLineNumber: position.lineNumber,
              startColumn: 1,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            });

            // Check if we're typing a JSX tag
            const tagMatch = textUntilPosition.match(
              /<([a-zA-Z][a-zA-Z0-9]*)$/
            );
            if (tagMatch) {
              const tagName = tagMatch[1];
              return {
                suggestions: [
                  {
                    label: `<${tagName} />`,
                    kind: monaco.languages.CompletionItemKind.Snippet,
                    insertText: `<${tagName} />`,
                    detail: "Self-closing tag",
                    range: {
                      startLineNumber: position.lineNumber,
                      startColumn: position.column - tagName.length - 1,
                      endLineNumber: position.lineNumber,
                      endColumn: position.column,
                    },
                  },
                  {
                    label: `<${tagName}></${tagName}>`,
                    kind: monaco.languages.CompletionItemKind.Snippet,
                    insertText: `<${tagName}>$1</${tagName}>`,
                    insertTextRules:
                      monaco.languages.CompletionItemInsertTextRule
                        .InsertAsSnippet,
                    detail: "Opening and closing tag",
                    range: {
                      startLineNumber: position.lineNumber,
                      startColumn: position.column - tagName.length - 1,
                      endLineNumber: position.lineNumber,
                      endColumn: position.column,
                    },
                  },
                ],
              };
            }

            return { suggestions: [] };
          },
        });
      }
    );

    // Auto-closing brackets and tags
    editor.onDidChangeModelContent((e) => {
      const changes = e.changes;
      changes.forEach((change) => {
        if (change.text === ">") {
          const position = editor.getPosition();
          const model = editor.getModel();
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Check if we just completed a self-closing tag
          const selfClosingMatch = textBeforeCursor.match(
            /<([a-zA-Z][a-zA-Z0-9]*)\s*>$/
          );
          if (
            selfClosingMatch &&
            !textBeforeCursor.includes(`</${selfClosingMatch[1]}>`)
          ) {
            const tagName = selfClosingMatch[1];
            // Insert closing tag
            editor.executeEdits("auto-close-tag", [
              {
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
                text: `</${tagName}>`,
              },
            ]);

            // Move cursor between tags
            editor.setPosition({
              lineNumber: position.lineNumber,
              column: position.column,
            });
          }
        }
      });
    });

    // Prevent zoom shortcuts
    editor.onKeyDown((event) => {
      const { keyCode, ctrlKey, metaKey } = event;
      if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
        event.preventDefault();
      }
    });
  };
  return (
    <Editor
      width="100%"
      height="100%"
      language={language}
      options={options}
      theme="vs-dark"
      key={sandpack.activeFile}
      value={code}
      defaultValue={code}
      onChange={(value) => updateCode(value || "")}
      onMount={onMount}
    />
  );
}
